import { flushSync } from 'react-dom';
import { createContext, type MouseEvent, type PropsWithChildren, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { getPreferencesObject } from '@pkgs/components/utils/aiSearchPreferenceUtils';
import { useQueryParams } from '@pkgs/components/hooks/useQueryParams';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { getLocationBreadcrumbString, isFullListing } from '@ev-tech/search-modules-api';

import { forwardSearchResultCardLinkEvent, getSearchResultCardLink } from './SearchResultCard.utils';
import {
  type SearchResultCardContextValue,
  type SearchResultCardForwardedLinkEvent,
  type SearchResultCardProviderCardProps,
  type PropertyCardLinkClickHandler,
} from './SearchResultCard.types';

const SearchResultCardContext = createContext<SearchResultCardContextValue | null>(null);

export const SearchResultCardProvider = ({ cardProps, children }: PropsWithChildren<{ cardProps: SearchResultCardProviderCardProps }>) => {
  const {
    listing,
    language,
    countryCode,
    hrefBaseUrl,
    linkParams,
    onClickPropertyCardLink,
    translationResolver,
    onContextMenu,
    searchMode,
  } = cardProps;

  const preferenceScoreOverall = isFullListing(listing) ? listing.preferenceScoreOverall : undefined;
  const preferenceScores = isFullListing(listing) ? listing.preferenceScores : undefined;
  const trackingQueryParams = useQueryParams();
  const linkRef = useRef<HTMLSpanElement | null>(null);
  const [isContextMenuLinkOverlayActive, setIsContextMenuLinkOverlayActive] = useState(false);

  const formattedLocation: string = useMemo(() => getLocationBreadcrumbString({ listing, returnAllItems: true }), [listing]);

  const link = useMemo(
    () =>
      getSearchResultCardLink({
        listing,
        formattedLocation,
        language,
        countryCode,
        hrefBaseUrl,
        linkParams,
        trackingQueryParams,
      }),
    [listing, formattedLocation, trackingQueryParams, language, countryCode, hrefBaseUrl, linkParams],
  );

  const handlePropertyCardLinkClick = useCallback<PropertyCardLinkClickHandler>(
    (e) => {
      e.stopPropagation();
      onClickPropertyCardLink?.(link.href);
    },
    [onClickPropertyCardLink, link.href],
  );

  const preferences = useMemo(() => {
    const canShowPreferences =
      searchMode === SearchMode.AI && preferenceScores && typeof preferenceScoreOverall === 'number' && preferenceScoreOverall >= 0.5;
    if (!canShowPreferences) {
      return undefined;
    } else {
      return getPreferencesObject({
        t: translationResolver,
        preferenceScores,
        preferenceScoreOverall,
      });
    }
  }, [searchMode, preferenceScores, preferenceScoreOverall, translationResolver]);

  const handleSearchResultCardLinkClick = useCallback(
    (event: SearchResultCardForwardedLinkEvent) => {
      forwardSearchResultCardLinkEvent({ event, linkRef });
    },
    [linkRef],
  );

  const handleContextMenuLinkOverlayActivate = useCallback(() => {
    flushSync(() => setIsContextMenuLinkOverlayActive(true));
  }, []);

  const handleContextMenuLinkOverlayDeactivate = useCallback(() => {
    setIsContextMenuLinkOverlayActive(false);
  }, []);

  const handleSearchResultCardContextMenu = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      onContextMenu?.(e);

      window.setTimeout(() => setIsContextMenuLinkOverlayActive(false), 0);
    },
    [onContextMenu],
  );

  const contextValue = useMemo<SearchResultCardContextValue>(
    () => ({
      ...cardProps,
      formattedLocation,
      isContextMenuLinkOverlayActive,
      link,
      linkRef,
      onContextMenuLinkOverlayActivate: handleContextMenuLinkOverlayActivate,
      onContextMenuLinkOverlayDeactivate: handleContextMenuLinkOverlayDeactivate,
      onClickPropertyCardLink: handlePropertyCardLinkClick,
      onSearchResultCardContextMenu: handleSearchResultCardContextMenu,
      onSearchResultCardLinkClick: handleSearchResultCardLinkClick,
      preferences,
    }),
    [
      cardProps,
      formattedLocation,
      handleContextMenuLinkOverlayActivate,
      handleContextMenuLinkOverlayDeactivate,
      handlePropertyCardLinkClick,
      handleSearchResultCardContextMenu,
      handleSearchResultCardLinkClick,
      isContextMenuLinkOverlayActive,
      link,
      linkRef,
      preferences,
    ],
  );

  return <SearchResultCardContext.Provider value={contextValue}>{children}</SearchResultCardContext.Provider>;
};

export const useSearchResultCardContext = () => {
  const context = useContext(SearchResultCardContext);

  if (!context) throw new Error('useSearchResultCardContext must be used within SearchResultCardProvider');

  return context;
};

export const useDefaultSearchResultCardContext = () => {
  const context = useSearchResultCardContext();

  if (context.cardType !== 'default') {
    throw new Error('DefaultSearchResultCard must be used with a default listing');
  }

  return context;
};

export const useRestrictedSearchResultCardContext = () => {
  const context = useSearchResultCardContext();

  if (context.cardType !== 'restricted') throw new Error('RestrictedSearchResultCard must be used with a restricted listing');

  return context;
};

export const useUAExclusiveSearchResultCardContext = () => {
  const context = useSearchResultCardContext();

  if (context.cardType !== 'uaExclusive') throw new Error('UAExclusiveSearchResultCard must be used with a uaExclusive listing');

  return context;
};
